import React, { FC, ReactNode, RefObject, useEffect, useRef } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { T } from 'src/shared/helpers';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import { ZTooltip, ZTooltipLookContext } from '../../../../widgets/ztooltip';
import { editorPalette, editorTheme, questionDialogTheme } from './editorPalette';
import { Dialog, DialogProps, DialogSize } from '../../../../widgets/dialog';
import { DropdownToneContext } from '../../../../widgets/dropdown';

// LA FINESTRA DEL PROTOTIPO (utente, 2026-10-20): 720 × 560, la testata da 44px con un filo sotto, e sotto il menu
// e le pagine fino ai bordi — non più la finestra grande del Backoffice con un riquadro dentro. È la cornice del Dialog
// (`windowDecorator`), così il componente condiviso resta com'è.
const SettingsWindowFrame = styled.div<{ $width: number; $height?: number; $padded: boolean }>`
    ${editorPalette}

    position: relative;
    display: flex;
    flex: 0 0 ${(props) => props.$width}px;
    flex-direction: column;
    max-width: calc(100vw - 32px);
    height: ${(props) => (props.$height ? `min(${props.$height}px, calc(100vh - 32px))` : 'auto')};
    max-height: calc(100vh - 32px);
    overflow: hidden;
    border-radius: 10px;
    background: var(--editor-panel);

    /* La finestra prende il fuoco solo quando non ha comandi: lo dice già la finestra stessa, senza contorno. */
    &:focus {
        outline: none;
    }

    > h1 {
        display: flex;
        flex: none;
        align-items: center;
        height: 44px;
        margin: 0;
        padding: 0 48px 0 16px;
        border-bottom: 1px solid var(--editor-sunken, #e9ebed);
        color: var(--editor-fg);
        font-size: 13px;
        font-weight: 500;
    }

    /* Le finestre piccole hanno il corpo col suo respiro; quella delle Settings va fino ai bordi. */
    ${(props) =>
        props.$padded &&
        css`
            /* Il corpo e il piede del Dialog del Backoffice, col respiro della finestra. */
            > h1 ~ div {
                padding-right: 16px;
                padding-left: 16px;
                color: var(--editor-fg);
            }

            > h1 + div {
                padding-top: 16px;
            }

            > :last-child {
                padding-bottom: 16px;
            }
        `}
`;

const SettingsWindowClose = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    display: grid;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    place-items: center;
    background: none;
    color: var(--editor-muted);
    cursor: pointer;

    &:hover {
        background: var(--editor-sunken, #e9ebed);
        color: var(--editor-fg);
    }

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Le finestre aperte, l'ultima in cima: solo quella in cima tiene il fuoco, così una domanda aperta dalle Settings
// non se lo vede rubare dalla finestra sotto.
const openWindows: HTMLElement[] = [];
let windowCount = 0;

/**
 * UNA FINESTRA SI FA TROVARE E NON SI LASCIA SCAPPARE (utente, 2026-10-22, accessibilità). Il Dialog del Backoffice non
 * dice di essere una finestra e non governa il fuoco; qui la cornice degli editor lo fa per le sue:
 *   - si presenta: `role="dialog"`, `aria-modal`, e il titolo (l'h1 della testata) come nome;
 *   - all'apertura il fuoco entra, sulla voce della pagina aperta o sul primo comando del corpo (la X solo se non ce ne sono altri);
 *   - Tab e Maiuscolo+Tab girano dentro la finestra e non finiscono nell'editor sotto il velo;
 *   - alla chiusura il fuoco torna dov'era, di solito sul comando che l'ha aperta.
 */
const useDialogFocus = (frameRef: RefObject<HTMLDivElement | null>) => {
    useEffect(() => {
        const frame = frameRef.current;
        if (!frame) return;
        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        openWindows.push(frame);

        const title = frame.querySelector('h1');
        if (title) {
            if (!title.id) title.id = `editor-dialog-title-${++windowCount}`;
            frame.setAttribute('aria-labelledby', title.id);
        }

        const focusables = () => Array.from(frame.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => element.offsetParent !== null);
        // La voce della pagina aperta (le Settings aperte su «Keyboard») prima del primo comando qualsiasi.
        const first = frame.querySelector<HTMLElement>('[aria-current="page"]') ?? focusables().find((element) => !element.hasAttribute('data-dialog-close'));
        (first ?? focusables()[0] ?? frame).focus();

        const keepFocusInside = (event: KeyboardEvent) => {
            if (event.key !== 'Tab' || openWindows[openWindows.length - 1] !== frame) return;
            const active = document.activeElement;
            // Una finestra del Backoffice aperta sopra questa tiene il suo fuoco. Il Dialog del Backoffice non ha `role`:
            // si riconosce dal suo velo (`data-dialog-overlay`), e se l'ultimo velo aperto non è quello di questa finestra
            // c'è una finestra sopra (l'avviso dell'AR, l'Upgrade dell'AI) e Tab deve poterci arrivare.
            if (active && !frame.contains(active) && active.closest('[role="dialog"], [aria-modal="true"]')) return;
            const overlays = document.querySelectorAll('[data-dialog-overlay]');
            const ownOverlay = frame.closest('[data-dialog-overlay]');
            if (ownOverlay && overlays.length && overlays[overlays.length - 1] !== ownOverlay) return;
            const list = focusables();
            if (list.length === 0) {
                event.preventDefault();
                frame.focus();
                return;
            }
            const firstElement = list[0];
            const lastElement = list[list.length - 1];
            if (!active || !frame.contains(active)) {
                event.preventDefault();
                (event.shiftKey ? lastElement : firstElement).focus();
            } else if (event.shiftKey && active === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && active === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };
        document.addEventListener('keydown', keepFocusInside);

        return () => {
            document.removeEventListener('keydown', keepFocusInside);
            openWindows.splice(openWindows.indexOf(frame), 1);
            if (opener && document.contains(opener)) opener.focus();
        };
    }, [frameRef]);
};

/**
 * LA CORNICE DELLE FINESTRE DEGLI EDITOR (prototipo): testata da 44px col filo, X con tooltip, colori degli editor. Si
 * passa al Dialog del Backoffice come `windowDecorator`. Le Settings la usano larga 720 × 560 e senza respiro nel corpo;
 * le finestre piccole (Measure scene, il QR dell'AR) a misura del contenuto.
 */
//
// I BOTTONI DELLE FINESTRE SONO QUELLI DI ZAKEKE (utente, 2026-10-21): Primary pieno per l'azione, Basic per l'alternativa,
// come lo Storybook e le finestre del Backoffice; il Primary nel pieno scuro degli editor, come Save (`questionDialogTheme`). `editorButtons` rimette il tema degli editor solo dove la finestra è un
// pannello di lavoro e non una domanda: le Settings.
export const editorDialogWindow = (width: number, height?: number, padded = true, editorButtons = false): FC<{ onClose: () => void; children?: ReactNode }> => {
    const EditorDialogWindow: FC<{ onClose: () => void; children?: ReactNode }> = ({ onClose, children }) => {
        const frameRef = useRef<HTMLDivElement>(null);
        useDialogFocus(frameRef);
        const frame = (
            <ZTooltipLookContext.Provider value='editor'>
                <DropdownToneContext.Provider value='soft'>
                    <SettingsWindowFrame ref={frameRef} role='dialog' aria-modal='true' tabIndex={-1} $width={width} $height={height} $padded={padded}>
                        <ZTooltip title={T._('Close · Esc', 'Admin')} position='bottom'>
                            <SettingsWindowClose type='button' data-dialog-close aria-label={T._('Close', 'Admin')} onClick={onClose}>
                                <CloseIcon />
                            </SettingsWindowClose>
                        </ZTooltip>
                        {children}
                    </SettingsWindowFrame>
                </DropdownToneContext.Provider>
            </ZTooltipLookContext.Provider>
        );
        return <ThemeProvider theme={editorButtons ? editorTheme : questionDialogTheme}>{frame}</ThemeProvider>;
    };
    return EditorDialogWindow;
};

// LE MISURE DEL BACKOFFICE NELLE FINESTRE DEGLI EDITOR: un po' più strette, come quelle del prototipo.
const EDITOR_DIALOG_WIDTH: Record<Exclude<DialogSize, 'fullscreen'>, number> = { small: 480, medium: 640, large: 880, xlarge: 1120 };
const editorWindows = Object.fromEntries(Object.entries(EDITOR_DIALOG_WIDTH).map(([size, width]) => [size, editorDialogWindow(width)])) as Record<
    Exclude<DialogSize, 'fullscreen'>,
    ReturnType<typeof editorDialogWindow>
>;

/**
 * LE FINESTRE DEGLI EDITOR (utente, 2026-10-20, ricetta «chiusura»): il Dialog del Backoffice con la cornice, la X e i
 * menu degli editor; i bottoni del piede sono quelli di Zakeke (2026-10-21). A schermo intero resta quella del Backoffice.
 */
export const EditorDialog = React.forwardRef<HTMLDivElement, DialogProps>((props, ref) =>
    props.size === 'fullscreen' ? <Dialog ref={ref} {...props} /> : <Dialog ref={ref} {...props} windowDecorator={editorWindows[props.size ?? 'medium']} />
);
EditorDialog.displayName = 'EditorDialog';
